import { useCallback, useEffect, useRef, useState } from "react";

import { useToggle } from "@/shared/hooks/useToggle";

import { PORTAL_DROPDOWN_ATTRIBUTE } from "./shared.styles";

type UseDropdownOptions = {
  name: string;
  onClose?: () => void;
  onOutsideClick?: () => void;
};

type Dropdown = {
  focusedIndex: number;
  focusedOptionId: string | undefined;
  isOpen: boolean;
  dropdownRef: React.RefObject<HTMLDivElement>;
  wrapperRef: React.RefObject<HTMLDivElement>;
  close: () => void;
  collapse: () => void;
  open: (focusIndex?: number) => void;
  setFocusedIndex: React.Dispatch<React.SetStateAction<number>>;
  toggle: () => void;
};

const useDropdown = ({
  name,
  onClose,
  onOutsideClick,
}: UseDropdownOptions): Dropdown => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const {
    on: isOpen,
    setOff: closeDropdown,
    setOn: openDropdown,
  } = useToggle();
  const [focusedIndex, setFocusedIndex] = useState(-1);

  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const onOutsideClickRef = useRef(onOutsideClick);
  onOutsideClickRef.current = onOutsideClick;

  const collapse = useCallback((): void => {
    closeDropdown();
    setFocusedIndex(-1);
  }, [closeDropdown]);

  const close = useCallback((): void => {
    collapse();
    onCloseRef.current?.();
  }, [collapse]);

  const open = (focusIndex = 0): void => {
    openDropdown();
    setFocusedIndex(focusIndex);
  };

  const toggle = (): void => {
    if (isOpen) {
      close();
    } else {
      open();
    }
  };

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (wrapperRef.current?.contains(target)) return;
      if (dropdownRef.current?.contains(target)) return;
      // A nested dropdown portals to document.body — a click inside it must
      // not count as an outside click for its parent (DEV-2117).
      const element = target instanceof Element ? target : target.parentElement;
      if (element?.closest(`[${PORTAL_DROPDOWN_ATTRIBUTE}]`)) return;
      const outsideClick = onOutsideClickRef.current;
      if (outsideClick) {
        outsideClick();
      } else {
        close();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, close]);

  useEffect(() => {
    if (!isOpen || focusedIndex < 0) return;
    dropdownRef.current
      ?.querySelector(`[data-index="${focusedIndex}"]`)
      ?.scrollIntoView({ block: "nearest" });
  }, [isOpen, focusedIndex]);

  const focusedOptionId =
    isOpen && focusedIndex >= 0 ? `${name}-option-${focusedIndex}` : undefined;

  return {
    close,
    collapse,
    dropdownRef,
    focusedIndex,
    focusedOptionId,
    isOpen,
    open,
    setFocusedIndex,
    toggle,
    wrapperRef,
  };
};

export { useDropdown, type Dropdown };
